import queryClient from '@src/app/providers/queryClient';
import type { PhoneBookUserDto } from '@src/features/users/model/query/usePhoneBook';
import type { UserStatsDto } from '@src/features/users/model/query/useUsers';
import { USER_PRESENCE_TOPIC } from '@src/features/users/model/realtime';
import type { AppQueryKey } from '@src/shared/query/utils';
import { type TopicMessage, useWebSocketTopic } from '@src/shared/websocket';
import { useCallback, useRef } from 'react';

// `urlToQueryKey` splits the URL on "/", leaving a trailing empty segment.
// Exact keys for the cache writes, prefixes for the reconnect reconciliation.
const STATS_QUERY_KEY: AppQueryKey = ['root', 'users', 'stats', ''];
const PHONE_BOOK_QUERY_KEY: AppQueryKey = ['root', 'users', 'phone-book', ''];
const STATS_QUERY_PREFIX: AppQueryKey = ['root', 'users', 'stats'];
const PHONE_BOOK_QUERY_PREFIX: AppQueryKey = ['root', 'users', 'phone-book'];

type PresenceSnapshot = {
  online: number[];
  readAt: number;
};

function parseSnapshot(message: TopicMessage): PresenceSnapshot | null {
  const { online, read_at: readAt } = message;
  if (!Array.isArray(online)) return null;
  return {
    online: online.filter((id): id is number => typeof id === 'number'),
    readAt: typeof readAt === 'number' ? readAt : 0,
  };
}

/**
 * Keeps the online count and the phone book dots live.
 *
 * Each message carries the whole online set, so it is applied directly to the
 * cache and never triggers a refetch. That matters because presence fans out
 * to every connected client: refetching per event would turn one person
 * opening the app into a request from every other browser.
 */
export default function UserPresenceSocketController() {
  // Reading the registry and publishing the snapshot are separate steps on
  // the server, so a disconnect can publish an older set after a reconnect
  // published a newer one. A self-contained message is not automatically an
  // ordered one; the server's read stamp is what settles it.
  const lastReadAt = useRef(0);

  const handleMessage = useCallback((message: TopicMessage) => {
    const snapshot = parseSnapshot(message);
    if (!snapshot) return;

    // A stamp of 0 means the server had no clock to read, so arrival order is
    // all we have and the snapshot is applied.
    if (snapshot.readAt > 0) {
      if (snapshot.readAt <= lastReadAt.current) return;
      lastReadAt.current = snapshot.readAt;
    }

    const online = new Set(snapshot.online);

    // Both numbers come from this one list, so the header and the phone book
    // cannot disagree. Leaving an absent cache untouched matters: seeding a
    // key that was never fetched would hand the UI a partial response.
    queryClient.setQueryData(
      STATS_QUERY_KEY,
      (stats: UserStatsDto | undefined) =>
        stats === undefined ? stats : { ...stats, online: online.size },
    );

    queryClient.setQueryData(
      PHONE_BOOK_QUERY_KEY,
      (users: PhoneBookUserDto[] | undefined) =>
        users === undefined
          ? users
          : users.map((user) =>
              user.online === online.has(user.id)
                ? user
                : { ...user, online: online.has(user.id) },
            ),
    );
  }, []);

  // Only when the socket (re)connects, where messages may have been missed.
  // This is per client and self-triggered, so it never fans out.
  const reconcile = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: STATS_QUERY_PREFIX });
    void queryClient.invalidateQueries({ queryKey: PHONE_BOOK_QUERY_PREFIX });
  }, []);

  useWebSocketTopic(USER_PRESENCE_TOPIC, handleMessage, {
    onReconnect: reconcile,
  });

  return null;
}
